import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IonContent } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';
import { TranslateService } from '@ngstack/translate';

import { ChangeAppLang } from '@shared/store/app.actions';

import { AUTH_LANGUAGES } from '../components/login/login.page';
import { WALKTHROUGH_PANE_COUNT } from './data/walkthrough.fixtures';

/**
 * `/walkthrough` — the eight-pane Introduction Walkthrough (specs/auth.md §5.4).
 *
 * ---------------------------------------------------------------------------
 * THIS CLASS IS THE SHELL, AND THE LIST BELOW IS EXHAUSTIVE (D31.3).
 *
 * What the shell owns:
 *   · the sticky bar — the lockup (a link to Signup), the EN/FR pill, `Skip`
 *   · the dot rail, built from `WALKTHROUGH_PANE_COUNT` rather than hardcoded
 *   · the `N / 8` counter
 *   · prev / next, including the LABEL FLIP on the last pane
 *   · arrow-key navigation
 *   · scroll-to-top on every pane change
 *
 * What it does NOT own: anything a pane says or draws. The eight bodies are
 * eight components, because the scout measured 8 of 8 differing STRUCTURALLY
 * and 0 by content alone — pane 1 has no copy/illustration split at all, no two
 * illustrations share a shape, and panes 3 and 4 wrap the same kind of chip at
 * different points in their sentence. A pane component taking eight configs is
 * the same mistake as a Setup pane growing a prop per module (D10, D31.3).
 * ---------------------------------------------------------------------------
 *
 * THERE IS NO STORE, and `auth.module.ts` already argues why for this feature:
 * the only object auth shares is the EN/FR preference, and `KzLocaleService`
 * owns it (§7 — "do not add a second key, a second signal or a per-page
 * toggle"). Which pane is on screen is view-local by definition — nothing
 * outside this page can read it and nothing should — so it is a `signal` here.
 *
 * ⚠ NOTHING ON THIS SCREEN AUTHENTICATES, and the last pane's button is the one
 * to watch. In the source it does `location.href = '…Signup…'`; here it routes
 * to `/signup`. It creates nothing, sets nothing and stores nothing — the word
 * `Create` is the copy on a marketing button, not a promise this page keeps.
 */
@Component({
  selector: 'app-walkthrough',
  standalone: false,
  templateUrl: './walkthrough.page.html',
  styleUrls: ['./walkthrough.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalkthroughPage {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  /**
   * `ion-content` is the scroller here, not `window`.
   *
   * The source calls `window.scrollTo({top:0})` because its page scrolls the
   * document. Inside Ionic the document does not scroll — `ion-content` owns an
   * inner scroll container — so `window.scrollTo` would silently do nothing and
   * pane 8 would open halfway down. Same behaviour, different scroller.
   */
  @ViewChild(IonContent) private readonly content?: IonContent;

  /** Eight. Not written out anywhere else, and the dots read it too. */
  readonly paneCount = WALKTHROUGH_PANE_COUNT;

  /** EN / FR — the same list Login and Signup bind. */
  readonly languages = AUTH_LANGUAGES;

  /**
   * The language the copy is RENDERED in. Written through `AppState` (`ChangeAppLang`), read back
   * from `@ngstack/translate`'s `activeLangChanged`, which fires only once the new language's
   * strings have loaded. Reading the store instead re-rendered the page before French arrived, and
   * nothing re-checked it afterwards. No local copy (§7).
   */
  readonly lang = toSignal(
    this.translate.activeLangChanged.pipe(map((change) => change.currentValue)),
    { initialValue: this.translate.activeLang || 'en' },
  );

  /** Zero-based. View-local: no other screen has any business reading it. */
  readonly index = signal(0);

  /**
   * Whether this page is the one on screen.
   *
   * ⚠ NOT REDUNDANT, and the reason is a framework difference the source cannot
   * have. `ion-router-outlet` maintains a NAVIGATION STACK: leaving for
   * `/signup` does not destroy this component, it hides it (`ion-page-hidden`)
   * and keeps it mounted. So the `window:keydown` listener below outlives the
   * page's visibility, and arrow keys pressed on Signup — including inside a
   * text field, where they only move the caret — would silently drive a
   * walkthrough nobody is looking at.
   *
   * In the source, navigating away unloads the document and takes the listener
   * with it. This flag reproduces that: the listener exists while the page is
   * on screen, and not otherwise. Ionic's own lifecycle hooks rather than a
   * class check, so nothing here depends on an internal class name.
   */
  private onScreen = true;

  /** Drives the button label flip and nothing else. */
  readonly isLast = computed(() => this.index() === this.paneCount - 1);

  /**
   * The eight dot labels, translated.
   *
   * `computed` on `lang()` — via `translate()`, which reads the signal — so the
   * labels re-resolve when the pill is toggled, exactly as the source's
   * `setLang` re-runs `paint()`. The source leaves these English for a French
   * reader (§8, §11 R7); translating them is the same deliberate improvement
   * Login and Signup made for their five labels.
   */
  readonly dotLabels = computed(() =>
    Array.from({ length: this.paneCount }, (_, n) =>
      // Reading lang() makes the labels re-resolve when the language changes.
      this.lang() && this.translate.get('app.pages.auth.walkthrough.dot.label', { n: n + 1 }),
    ),
  );

  /**
   * Ionic lifecycle. Fires on every entry, including a return from Signup.
   *
   * Resets to pane 1, which is what the source does — each of its three screens
   * is a separate document, so arriving at the walkthrough always starts at the
   * beginning (§5.4: *"Load, no param, no storage → Pane 1 of 8"*). Under
   * `ion-router-outlet` the component survives the round trip and would
   * otherwise resume on whichever pane the user skipped from. Resuming is not a
   * decision the source made; it is a side effect of the stack, so it is not
   * inherited.
   */
  ionViewWillEnter(): void {
    this.onScreen = true;
    this.index.set(0);
  }

  /** Leaving the stack: stop answering the arrow keys. See `onScreen`. */
  ionViewWillLeave(): void {
    this.onScreen = false;
  }

  /**
   * Jump to a pane. Clamped, then scrolled to top — both verbatim from the
   * source's `go()`, which clamps to `[0, 7]` and scrolls unconditionally.
   */
  go(index: number): void {
    const clamped = Math.max(0, Math.min(this.paneCount - 1, index));
    this.index.set(clamped);
    void this.content?.scrollToTop();
  }

  /** `#prev`. Disabled on pane 1, so the clamp is a second line of defence. */
  previous(): void {
    this.go(this.index() - 1);
  }

  /**
   * `#next`. Advances on panes 1–7; on pane 8 it leaves for Signup.
   *
   * The source uses `location.href`, which is why the spec notes there is no
   * middle-click and no new tab. A `routerLink` would have given both — but it
   * would also have made the primary button an `<a>` on eight panes and a
   * navigation on one, so the button stays a button and this method decides.
   */
  next(): void {
    if (this.isLast()) {
      void this.router.navigate(['/signup']);
      return;
    }
    this.go(this.index() + 1);
  }

  /**
   * Arrow-key navigation, on the WINDOW.
   *
   * Ported with the source's behaviour intact, including the part that looks
   * like an oversight: there is no focus guard, so the arrows work while the
   * EN/FR buttons have focus. That is correct here — the page has no text input
   * and no horizontally scrollable region, so nothing else wants the arrows,
   * and a guard would break keyboard navigation for someone who has just
   * tabbed to the language pill.
   *
   * Angular removes the listener when the page is destroyed — but under
   * `ion-router-outlet` that is later than you would think, which is what
   * `onScreen` is for.
   */
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.onScreen) {
      return;
    }
    if (event.key === 'ArrowRight') {
      this.go(this.index() + 1);
    } else if (event.key === 'ArrowLeft') {
      this.go(this.index() - 1);
    }
  }

  /** Writes the shared preference through the store, as Login and Signup do. */
  onLanguageChange(code: string): void {
    if (code === 'en' || code === 'fr') {
      this.store.dispatch(new ChangeAppLang(code));
    }
  }
}
